import { useMemo } from 'react';
import type { TreeConditionNode } from '@/components/tree-dnd/tree-dnd-types';
import {
  MOCK_LOCATION_OPTIONS,
  MOCK_RFM_GROUP_OPTIONS,
} from '@/components/tree-dnd/tree-dnd-conditions/mock-options';
import { useCampaignMatchListOptions } from '@/components/tree-dnd/tree-dnd-conditions/campaign-match-list';
import { useProductMatchListOptions } from '@/components/tree-dnd/tree-dnd-conditions/product-match-list';
import { useRfmGroupLabels } from '@/components/tree-dnd/tree-dnd-conditions/rfm-group-grid';
import {
  useDateFilterSummary,
  useMatchListSummary,
  useNumberPredicateSummary,
  useTotalSpendSummary,
} from '@/components/tree-dnd/tree-dnd-conditions/use-condition-summaries';
import { useI18nContext } from '@/i18n/i18n-react';

/** Live summary of what an enabled condition is filtering for. */
export function useConditionLiveSummary(node: TreeConditionNode): string {
  const { LL } = useI18nContext();
  const rfmLabels = useRfmGroupLabels();

  const localizedRfmOptions = useMemo(
    () =>
      MOCK_RFM_GROUP_OPTIONS.map((option) => ({
        ...option,
        label: rfmLabels[option.id as keyof typeof rfmLabels] ?? option.label,
      })),
    [rfmLabels]
  );

  const dateWhen =
    node.kind === 'installedApp' ||
    node.kind === 'boughtProduct' ||
    node.kind === 'lastNotificationAt' ||
    node.kind === 'lastAppAccessAt' ||
    node.kind === 'viewedProduct' ||
    node.kind === 'lastOrder' ||
    node.kind === 'orderFrequency'
      ? node.args.when
      : { type: 'anytime' as const };

  const products =
    node.kind === 'boughtProduct' || node.kind === 'viewedProduct'
      ? node.args.products
      : { match: 'any' as const, ids: [] as string[] };

  const campaigns =
    node.kind === 'receivedNotification' || node.kind === 'clickedCampaign'
      ? node.args.campaigns
      : { match: 'any' as const, ids: [] as string[] };

  const rfmGroups =
    node.kind === 'rfmGroup'
      ? { ...node.args.groups, match: 'any' as const }
      : { match: 'any' as const, ids: [] as string[] };

  const locations =
    node.kind === 'location'
      ? node.args.locations
      : { match: 'any' as const, ids: [] as string[] };

  const orderCount =
    node.kind === 'orderFrequency'
      ? node.args.count
      : { kind: 'atLeast' as const, value: 1 };

  const totalSpendAmount =
    node.kind === 'totalSpend'
      ? node.args.amount
      : { kind: 'atLeast' as const, value: 1 };

  const whenSummary = useDateFilterSummary(dateWhen);
  const productOptions = useProductMatchListOptions(products.ids);
  const campaignOptions = useCampaignMatchListOptions(campaigns.ids);
  const productsSummary = useMatchListSummary(
    products,
    productOptions,
    LL.audiences.filters.conditionSummary.noProductsSelected()
  );
  const campaignsSummary = useMatchListSummary(
    campaigns,
    campaignOptions,
    LL.audiences.filters.conditionSummary.noCampaignsSelected()
  );
  const rfmSummary = useMatchListSummary(
    rfmGroups,
    localizedRfmOptions,
    LL.audiences.filters.conditionSummary.noRfmGroupsSelected(),
    true
  );
  const locationsSummary = useMatchListSummary(
    locations,
    MOCK_LOCATION_OPTIONS,
    LL.audiences.filters.conditionSummary.noLocationsSelected()
  );
  const orderCountSummary = useNumberPredicateSummary(orderCount);
  const totalSpendSummary = useTotalSpendSummary(totalSpendAmount);

  switch (node.kind) {
    case 'installedApp':
    case 'lastNotificationAt':
    case 'lastAppAccessAt':
    case 'lastOrder':
      return whenSummary;
    case 'boughtProduct':
    case 'viewedProduct':
      return `${productsSummary} · ${whenSummary}`;
    case 'receivedNotification':
    case 'clickedCampaign':
      return campaignsSummary;
    case 'rfmGroup':
      return rfmSummary;
    case 'location':
      return locationsSummary;
    case 'orderFrequency':
      return `${orderCountSummary} · ${whenSummary}`;
    case 'totalSpend':
      return totalSpendSummary;
    case 'hasNotificationsEnabled':
      return LL.audiences.filters.conditionSummary.hasNotificationsEnabledSummary();
  }
}
